import { useMemo, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Card, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { DataGridPremium, type GridColDef, type GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { profitShareService, type OverviewRow } from '../../services/profit-share.service';
import { useAuthStore } from '../../stores/auth.store';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { STATUS, money as fmt, monthLabel, reportLink, shiftMonth, signedSx, thisMonth } from './profit-share.format';

const money = (field: string, headerName: string, opts: Partial<GridColDef> = {}): GridColDef => ({
  field, headerName, type: 'number', flex: 1, minWidth: 100, valueFormatter: (v: number) => fmt(v ?? 0), ...opts,
});

const COLUMNS: GridColDef<OverviewRow>[] = [
  { field: 'workspace_name', headerName: 'Workspace', flex: 1.5, minWidth: 150 },
  {
    field: 'status', headerName: 'Status', width: 100,
    renderCell: ({ row }) => (row.workspace_id === '__totals__' ? null : row.share_pct == null
      ? <Typography variant="caption" color="text.secondary">Ours</Typography>
      : <Chip size="small" label={STATUS[row.status].label} color={STATUS[row.status].color} variant={row.status === 'estimate' ? 'outlined' : 'filled'} />),
  },
  { field: 'share_pct', headerName: 'Share', width: 70, type: 'number', valueFormatter: (v: number | null) => (v == null ? '—' : `${v}%`) },
  money('revenue', 'Revenue'),
  money('cost', 'Ad cost'),
  money('adjustments', 'Adjustments'),
  money('expenses', 'Expenses'),
  money('net', 'Net', { renderCell: ({ value }) => <Box sx={signedSx(value)}>{fmt(value)}</Box> }),
  money('payout', 'Partner payout'),
  money('carry_out', 'Carried loss', { description: 'Loss the partner must earn back before the next payout.' }),
  money('company_profit', 'Company profit', { minWidth: 120, renderCell: ({ value }) => <Box sx={signedSx(value)}>{fmt(value)}</Box> }),
];

function Stat({ label, value, hint, tone }: { label: string; value: number; hint: string; tone?: boolean }) {
  return (
    <Card variant="outlined" sx={{ p: 1.5, minWidth: 180, flex: '1 1 180px' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={600} sx={tone ? signedSx(value) : undefined}>{fmt(value)}</Typography>
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
    </Card>
  );
}

export default function ProfitSharePage() {
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const navigate = useNavigate();
  const [month, setMonth] = useUrlState<string>('month', thisMonth(), codecs.str);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const { data, isLoading, error } = useQuery({
    queryKey: ['profit-share', 'overview', month],
    queryFn: () => profitShareService.overview(month),
    enabled: isAdmin,
  });

  const totalsRow = useMemo(() => (data ? [{
    ...data.totals, workspace_id: '__totals__', workspace_name: 'Total', status: 'estimate', share_pct: null,
  }] : undefined), [data]);

  if (!isAdmin) return <Alert severity="warning">You do not have access to this page.</Alert>;
  const running = month === thisMonth();
  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h4" fontWeight={600}>Profit share</Typography>
            <Typography variant="body2" color="text.secondary">
              What each workspace made, the partners’ share, and what the company keeps. Months are UTC;{' '}
              <Link component={RouterLink} to={reportLink(month)}>see this month in the report</Link>.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft /></IconButton>
            <Typography variant="h6" sx={{ minWidth: 110, textAlign: 'center' }} data-testid="profit-share-month">{monthLabel(month)}</Typography>
            <Tooltip title={running ? 'This month is still running' : ''}>
              <span><IconButton aria-label="Next month" disabled={running} onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight /></IconButton></span>
            </Tooltip>
          </Stack>
        </Box>
        {running && <Alert severity="info" sx={{ mb: 2 }}>This month is still running: its numbers are a live estimate.</Alert>}
        {data && (
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Stat label="Company profit" value={data.totals.company_profit} hint="Net after partner payouts" tone />
            <Stat label="Partner payouts" value={data.totals.payout} hint="Owed for this month" />
            <Stat label="Carried losses" value={data.totals.carry_out} hint="To be earned back before partners are paid again" />
          </Stack>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load the profit share.</Alert>}
      <DataGridPremium
        rows={data?.rows ?? []}
        columns={COLUMNS}
        getRowId={(r: OverviewRow) => r.workspace_id}
        loading={isLoading}
        density="compact"
        pinnedRows={totalsRow ? { bottom: totalsRow as any } : undefined}
        onRowClick={({ row }: GridRowParams<OverviewRow>) => row.workspace_id !== '__totals__' && navigate(`/profit-share/${row.workspace_id}`)}
        disableRowSelectionOnClick
        hideFooter
        initialState={{ pinnedColumns: { left: ['workspace_name'] } }}
        sx={{ height: gridHeight, '& .MuiDataGrid-row:not(.MuiDataGrid-row--pinned)': { cursor: 'pointer' } }}
      />
    </Box>
  );
}
